import type { ReactNode } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, BookOpen, Check, Trophy } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { CompTag } from '@/components/CompTag';
import { Countdown, useCountdown } from '@/components/Countdown';
import { MarkdownProse } from '@/components/MarkdownProse';
import { SiteHeader } from '@/components/SiteHeader';
import { useCompetition, type CompetitionSummary } from '@/hooks/useCompetitions';
import { useMySubmissions } from '@/hooks/useMySubmissions';
import { useAuth } from '@/contexts/AuthContext';
import { resolveGrandPrize } from '@/lib/grandPrize';
import { formatDate } from '@/lib/utils';
import { cn } from '@/lib/utils';

export function CompetitionDetailPage() {
	const { slug } = useParams();
	const { user } = useAuth();
	const { data: competition, isLoading } = useCompetition(slug);

	const { data: mySubs } = useMySubmissions(competition?.Id);
	const myActive = (mySubs ?? []).filter((s) => s.Status !== 'withdrawn');

	if (isLoading)
		return (
			<>
				<SiteHeader />
				<main className="mx-auto max-w-[1120px] px-10 py-8 text-loomlite-muted">Loading…</main>
			</>
		);
	if (!competition) return <Navigate to="/" replace />;

	const submissionOpen = competition.Status === 'open';
	const announced = competition.Status === 'announced';
	const submitHref = `/competitions/${competition.Slug}/submit`;
	const slotsLeft = Math.max(0, competition.MaxEntriesPerUser - myActive.length);

	return (
		<>
			<SiteHeader />
			<main className="mx-auto max-w-[1120px] px-5 pb-14 pt-8 md:px-10">
				<Button
					variant="ghost"
					size="sm"
					asChild
					className="mb-4 -ml-2 text-loomlite-dim hover:text-loomlite-text"
				>
					<Link to="/">
						<ArrowLeft className="mr-1 h-3.5 w-3.5" /> Back to Browse
					</Link>
				</Button>

				{/* Hero */}
				<section className="mb-9 grid items-stretch gap-8 lg:grid-cols-[1.4fr_1fr]">
					<div className="flex flex-col justify-between">
						<div>
							<div className="mb-4 flex flex-wrap items-center gap-2.5">
								<StatusPill status={competition.Status} />
								{competition.GameSystemFilter && (
									<CompTag color="muted" withDot={false}>
										{competition.GameSystemFilter}
									</CompTag>
								)}
								{submissionOpen && (
									<CompTag color="gold" withDot={false}>
										Featured
									</CompTag>
								)}
							</div>
							<h1
								className="m-0 font-heading text-[44px] font-bold leading-[1.02] tracking-[-0.025em] md:text-[56px]"
								style={{
									backgroundImage:
										'linear-gradient(96deg, #F8FAFC 0%, #F8FAFC 38%, #A855F7 75%, #FFB347 100%)',
									WebkitBackgroundClip: 'text',
									backgroundClip: 'text',
									color: 'transparent',
								}}
							>
								{competition.Name}
							</h1>
							<div className="mt-2.5 font-heading text-base text-loomlite-muted md:text-lg">
								{[competition.GameSystemFilter, formatTypeList(competition.AllowedEntityTypes)]
									.filter(Boolean)
									.join(' · ') || 'Open competition'}
							</div>
							{competition.Description && (
								<p
									className="mt-5 max-w-[540px] text-base leading-[1.6] text-loomlite-muted"
									style={{ textWrap: 'pretty' }}
								>
									{competition.Description}
								</p>
							)}
						</div>

						<div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-loomlite-dim">
							<Stat
								value={competition.MaxEntriesPerUser}
								unit={`${competition.MaxEntriesPerUser === 1 ? 'entry' : 'entries'} / user`}
							/>
							<span className="hidden h-6 w-px bg-loomlite-border md:block" />
							{competition.MaxTotalEntries ? (
								<>
									<Stat value={competition.MaxTotalEntries} unit="total cap" />
									<span className="hidden h-6 w-px bg-loomlite-border md:block" />
								</>
							) : null}
							<div>
								Hosted by <span className="text-loomlite-text">The Loomlite Editors</span>
							</div>
						</div>
					</div>

					{/* Sticky entry card */}
					<aside
						className="flex flex-col gap-4 rounded-xl border border-loomlite-purple/35 p-5"
						style={{
							background:
								'linear-gradient(180deg, rgba(131,1,196,0.16), rgba(131,1,196,0.04) 60%, var(--color-loomlite-card))',
						}}
					>
						<div className="text-[11px] uppercase tracking-[0.12em] text-[#D9A9F5]">
							{submissionOpen
								? 'Closes in'
								: announced
									? 'Winners announced'
									: 'Submissions closed'}
						</div>
						{submissionOpen ? (
							<Countdown targetIso={competition.SubmissionCloseAt} />
						) : (
							<div className="font-heading text-2xl font-semibold text-loomlite-text">
								{formatDate(announced ? competition.AnnouncementAt : competition.SubmissionCloseAt)}
							</div>
						)}

						{submissionOpen ? (
							user ? (
								<Button asChild className="px-4 py-3 text-[15px] font-semibold">
									<Link to={submitHref}>
										Submit an entry
										<ArrowRight className="h-3.5 w-3.5" />
									</Link>
								</Button>
							) : (
								<Button asChild className="px-4 py-3 text-[15px] font-semibold">
									<Link to="/login" state={{ from: submitHref }}>
										Sign in to submit
										<ArrowRight className="h-3.5 w-3.5" />
									</Link>
								</Button>
							)
						) : announced ? (
							<Button asChild variant="outline" className="px-4 py-3 text-[15px] font-semibold">
								<Link to={`/competitions/${competition.Slug}/announcement`}>
									See winners
									<ArrowRight className="h-3.5 w-3.5" />
								</Link>
							</Button>
						) : (
							<Button disabled className="px-4 py-3 text-[15px] font-semibold opacity-60">
								Submissions closed
							</Button>
						)}

						<div className="flex justify-between border-t border-loomlite-border pt-3.5 text-xs text-loomlite-dim">
							<span>
								{slotsLeft} of {competition.MaxEntriesPerUser} slot
								{competition.MaxEntriesPerUser === 1 ? '' : 's'} remaining
							</span>
							<span className="text-loomlite-muted">
								{user
									? myActive.length > 0
										? "You're entered"
										: "You haven't entered yet"
									: 'Sign in to enter'}
							</span>
						</div>
					</aside>
				</section>

				{/* Grand prize — pulled to the top screen to draw attention (LL-418e) */}
				<GrandPrizeBand competition={competition} />

				{/* Body */}
				<section className="grid gap-5 lg:grid-cols-[1.6fr_1fr]">
					<div className="rounded-xl border border-loomlite-border bg-loomlite-card p-6">
						<SectionHead
							title="Creative brief"
							sub="What this competition is asking for."
							icon={<BookOpen className="h-4 w-4" />}
						/>
						{competition.Brief && competition.Brief.trim().length > 0 ? (
							<div className="mt-4">
								<MarkdownProse>{competition.Brief}</MarkdownProse>
							</div>
						) : (
							<p className="mt-3 text-sm text-loomlite-muted">
								Creative brief publishes when the competition opens.
							</p>
						)}
					</div>

					<div className="flex flex-col gap-3.5">
						<FactCard label="Timeline">
							<FactRow k="Opens" v={formatDate(competition.SubmissionOpenAt)} />
							<FactRow k="Closes" v={<ClosesValue iso={competition.SubmissionCloseAt} />} />
							{competition.AnnouncementAt && (
								<FactRow k="Winner" v={formatDate(competition.AnnouncementAt)} />
							)}
						</FactCard>

						<FactCard label="Eligibility">
							<FactRow
								k="Cap"
								v={
									competition.MaxTotalEntries
										? `${competition.MaxEntriesPerUser} per user · ${competition.MaxTotalEntries} total`
										: `${competition.MaxEntriesPerUser} per user`
								}
							/>
							<FactRow
								k="Entry types"
								v={
									(competition.AllowedEntityTypes ?? []).length > 0 ? (
										<span className="flex flex-wrap justify-end gap-1.5">
											{(competition.AllowedEntityTypes ?? []).map((t) => (
												<CompTag key={t} color="purple" withDot={false}>
													{t}
												</CompTag>
											))}
										</span>
									) : (
										'Any'
									)
								}
							/>
							<FactRow k="Visibility" v="Public after deadline" />
						</FactCard>
					</div>
				</section>
			</main>
		</>
	);
}

function GrandPrizeBand({ competition }: { competition: CompetitionSummary }) {
	const prize = resolveGrandPrize(competition);
	return (
		<section
			className="mb-9 overflow-hidden rounded-xl border border-loomlite-gold/40 p-6"
			style={{
				background:
					'linear-gradient(120deg, rgba(255,140,0,0.10) 0%, rgba(255,140,0,0.03) 45%, var(--color-loomlite-card) 100%)',
			}}
		>
			<div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
				<div className="max-w-[560px]">
					<div className="mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-loomlite-gold-soft">
						<Trophy className="h-4 w-4" /> Grand prize
					</div>
					<div className="font-heading text-xl font-semibold leading-snug text-loomlite-text md:text-2xl">
						{prize.headline}
					</div>
				</div>
				<ul className="m-0 flex list-none flex-col gap-2 p-0 text-sm text-loomlite-muted md:min-w-[280px]">
					{prize.perks.map((perk) => (
						<li key={perk} className="flex gap-2">
							<Check
								className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-loomlite-green"
								strokeWidth={3}
							/>
							{perk}
						</li>
					))}
				</ul>
			</div>
		</section>
	);
}

function formatTypeList(types: string[] | null | undefined): string {
	if (!types || types.length === 0) return '';
	if (types.length === 1) return `${types[0]}s`;
	return types.join(' · ');
}

function StatusPill({ status }: { status: string }) {
	const open = status === 'open';
	const announced = status === 'announced';
	const color = open ? 'green' : announced ? 'gold' : 'muted';
	const label = open
		? 'Open'
		: announced
			? 'Announced'
			: status === 'judging'
				? 'Judging'
				: 'Closed';
	return <CompTag color={color}>{label}</CompTag>;
}

function Stat({ value, unit }: { value: number; unit: string }) {
	return (
		<div className="flex items-baseline gap-1.5">
			<span className="font-heading text-[22px] font-semibold text-loomlite-text tabular-nums">
				{value}
			</span>
			<span>{unit}</span>
		</div>
	);
}

function ClosesValue({ iso }: { iso: string }) {
	const v = useCountdown(iso);
	const label = formatDate(iso);
	if (v.expired) return <>{label}</>;
	return (
		<span className="inline-flex flex-col items-end leading-tight">
			<span>{label}</span>
			<span className="text-[11px] text-loomlite-gold-soft tabular-nums">
				{v.days}d {String(v.hours).padStart(2, '0')}h left
			</span>
		</span>
	);
}

interface SectionHeadProps {
	title: string;
	sub?: string;
	icon?: ReactNode;
}

function SectionHead({ title, sub, icon }: SectionHeadProps) {
	return (
		<div>
			<div className="flex items-center gap-2.5">
				{icon && <span className="text-loomlite-purple-soft">{icon}</span>}
				<h2 className="m-0 font-heading text-xl font-semibold tracking-[-0.01em] text-loomlite-text">
					{title}
				</h2>
			</div>
			{sub && (
				<div className={cn('mt-1.5 text-[13px] text-loomlite-muted', icon && 'ml-7')}>{sub}</div>
			)}
		</div>
	);
}

function FactCard({ label, children }: { label: string; children: ReactNode }) {
	return (
		<div className="rounded-xl border border-loomlite-border bg-loomlite-card p-[18px]">
			<div className="mb-3 text-[11px] uppercase tracking-[0.12em] text-loomlite-dim">{label}</div>
			<div className="flex flex-col gap-2.5">{children}</div>
		</div>
	);
}

function FactRow({ k, v }: { k: string; v: ReactNode }) {
	return (
		<div className="flex items-center justify-between gap-3 text-[13px]">
			<span className="text-loomlite-dim">{k}</span>
			<span className="text-right text-loomlite-text">{v}</span>
		</div>
	);
}
